import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IRequirementResponse, RequirementType } from "@miplatform/types";
import { formatDate } from "@miplatform/utils";
import {
  mdiHexagonOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePermissions, useRequirements } from "@mibuildweb/hooks";
import { Box, Typography, useTheme } from "@mui/material";

const CONTEXT_LABEL: Record<RequirementType, string> = {
  Inspection: "Inspection Authority",
  Community: "Community",
  Builder: "Builder",
};

function getContextValue(row: IRequirementResponse): string {
  return (
    row.inspectionAuthorityName ?? row.communityName ?? row.accountName ?? "--"
  );
}

export function Requirements() {
  const [selectedRequirement, setSelectedRequirement] =
    useState<IRequirementResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const theme = useTheme();
  const { navigate } = useRouter();
  const { hasPermission } = usePermissions();

  const canCreateRequirement = hasPermission(PERMISSIONS.REQUIREMENT.CREATE);
  const canUpdateRequirement = hasPermission(PERMISSIONS.REQUIREMENT.UPDATE);
  const canDeleteRequirement = hasPermission(PERMISSIONS.REQUIREMENT.DELETE);

  const {
    requirements,
    recentRequirements,
    refresh,
    isLoading,
    getRequirements,
    getRecentRequirements,
    deleteRequirement,
  } = useRequirements();

  const sections = [
    {
      fieldset: [
        {
          renderCell: (row: IRequirementResponse) => (
            <Box display="flex" flexDirection="column" py={1.5} pl={1}>
              <Typography variant="HD2" color={theme.palette.primary.main}>
                {row.name}
              </Typography>
              {row.modifiedOn && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="HD1">Last Updated:</Typography>
                  <Typography variant="HD1" fontWeight={400}>
                    {formatDate(row.modifiedOn, "MM/DD/YYYY")}
                  </Typography>
                </Box>
              )}
            </Box>
          ),
          flexSize: 2,
        },
        {
          renderCell: (row: IRequirementResponse) => (
            <Box display="flex" flexDirection="column" py={1.5} pl={1}>
              <Typography variant="HD1" color={theme.palette.text.secondary}>
                {CONTEXT_LABEL[row.type]}
              </Typography>
              <Typography variant="BD1">{getContextValue(row)}</Typography>
            </Box>
          ),
          flexSize: 2,
        },
        {
          renderCell: (row: IRequirementResponse) => (
            <Box display="flex" flexDirection="column" py={1.5} pl={1}>
              <Typography variant="HD1" color={theme.palette.text.secondary}>
                Items
              </Typography>
              <Typography variant="BD1">
                {row.materialNames?.join(", ") || "--"}
              </Typography>
            </Box>
          ),
          flexSize: 3,
        },
        {
          renderCell: (row: IRequirementResponse) => (
            <Box display="flex" flexDirection="column" py={1.5} pl={1}>
              <Typography variant="HD1" color={theme.palette.text.secondary}>
                Steps
              </Typography>
              <Typography variant="BD1">
                {row.stepNames?.join(", ") || "--"}
              </Typography>
            </Box>
          ),
          flexSize: 2,
        },
      ],
    },
  ];

  const handleNavigate = (item: IRequirementResponse) => {
    if (item.id) {
      navigate(`/estimating/configuration/requirements/${item.id}`);
    }
  };

  const renderItemActions = (item: IRequirementResponse) => {
    const menuData = [
      canUpdateRequirement && {
        label: BUTTON_LABELS.EDIT,
        icon: mdiPencilOutline,
        action: () => handleNavigate(item),
      },
      canDeleteRequirement && {
        label: BUTTON_LABELS.DELETE,
        icon: mdiTrashCanOutline,
        action: () => handleOpenDeleteModal(item),
        color: theme.palette.red.main,
      },
    ].filter(Boolean);

    if (menuData.length === 0) return null;

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu data={menuData} />
      </Box>
    );
  };

  const handleOpenDeleteModal = (item: IRequirementResponse) => {
    setSelectedRequirement(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedRequirement) {
      deleteRequirement(selectedRequirement);
      setOpenDeleteModal(false);
    }
  };

  return (
    <>
      <MiBuildPageHeader
        title="Requirements"
        loading={isLoading}
        titleIcon={
          <MiIcon path={mdiHexagonOutline} color={theme.palette.primary.main} />
        }
        search={search}
        onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearch(e.target.value)
        }
        searchPlaceholder="Search"
        actionComponent={
          canCreateRequirement ? (
            <MiButton
              title="Add Requirement"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{ path: mdiPlus }}
              onClick={() =>
                navigate("/estimating/configuration/add-requirements")
              }
              data-testid="add-requirement-btn"
            />
          ) : undefined
        }
        actionContainerStyle={{ flexWrap: "nowrap" }}
      />
      <MiBox>
        <MiList
          viewType="card"
          groupedMode
          groupedCriteria="type"
          showGroupCount
          groupedTitle={(type: string) => `${type} Requirement`}
          cardProps={{
            sections,
            renderItemActions,
            onClick: handleNavigate,
            leftCardProps: { fieldset: [] },
          }}
          loading={isLoading}
          data={requirements}
          dataProvider={getRequirements}
          recentData={recentRequirements}
          recentDataProvider={getRecentRequirements}
          refresh={refresh}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          emptyListProps={{ text: "No Requirements found" }}
          data-testid="requirements-list"
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          open={openDeleteModal}
          textPositive={BUTTON_LABELS.DELETE}
          buttonPositiveColor={BUTTON_COLORS.RED}
          titleIcon={{
            path: mdiTrashCanOutline,
            color: theme.palette.red.main,
          }}
          handleNegative={() => setOpenDeleteModal(false)}
          handlePositive={handleConfirmDeleteModal}
          description={`You are about to delete ${selectedRequirement?.name}. Are you sure you would like to continue?`}
        />
      </MiBox>
    </>
  );
}
